import React, { useState } from 'react';
import { useEstablishment } from '../../context/EstablishmentContext';
import { 
  Building2, 
  ChevronDown, 
  Check, 
  MapPin,
  Plus,
  Settings
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function EstablishmentSelector() {
  const { 
    establishments, 
    currentEstablishment, 
    hasGroup, 
    loading,
    switchEstablishment,
    canManageEstablishments 
  } = useEstablishment();
  const [isOpen, setIsOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const navigate = useNavigate();

  // Don't show if only one establishment and no group
  if (!hasGroup && establishments.length <= 1) {
    return null;
  }

  if (loading) {
    return (
      <div className="h-10 w-48 bg-gray-100 animate-pulse rounded-lg" />
    );
  }

  const handleSwitch = async (establishment) => {
    if (establishment.id === currentEstablishment?.id) {
      setIsOpen(false);
      return;
    }

    try {
      setSwitching(true);
      await switchEstablishment(establishment.id);
    } catch (err) {
      console.error('Switch failed:', err);
    } finally {
      setSwitching(false);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={switching}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors min-w-[200px]"
      >
        <Building2 className="w-4 h-4 text-emerald-600" />
        <div className="flex-1 text-left">
          <p className="text-sm font-medium text-gray-900 truncate max-w-[150px]">
            {currentEstablishment?.name || 'Sélectionner'}
          </p>
          {currentEstablishment?.city && (
            <p className="text-xs text-gray-500 truncate max-w-[150px]">
              {currentEstablishment.city}
            </p>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
            <div className="p-2 border-b border-gray-100">
              <p className="text-xs font-medium text-gray-500 px-2">
                Mes établissements ({establishments.length})
              </p>
            </div>
            
            <div className="max-h-64 overflow-y-auto p-2">
              {establishments.map((establishment) => (
                <button
                  key={establishment.id}
                  onClick={() => handleSwitch(establishment)}
                  disabled={switching}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    establishment.id === currentEstablishment?.id
                      ? 'bg-emerald-50 border border-emerald-200'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    establishment.id === currentEstablishment?.id
                      ? 'bg-emerald-100'
                      : 'bg-gray-100'
                  }`}>
                    <Building2 className={`w-4 h-4 ${
                      establishment.id === currentEstablishment?.id
                        ? 'text-emerald-600'
                        : 'text-gray-500'
                    }`} />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-gray-900">
                      {establishment.name}
                      {establishment.is_main && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">
                          Principal
                        </span>
                      )}
                    </p>
                    {establishment.city && (
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {establishment.address ? `${establishment.address}, ` : ''}{establishment.city}
                      </p>
                    )}
                  </div>
                  {establishment.id === currentEstablishment?.id && (
                    <Check className="w-4 h-4 text-emerald-600" />
                  )}
                </button>
              ))}
            </div>
            
            {canManageEstablishments && (
              <div className="p-2 border-t border-gray-100 space-y-1">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/restaurant/multi-site');
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter un établissement
                </button>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/restaurant/multi-site');
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  Gérer mes établissements
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
